import {styled} from '@linaria/react';
import React, {ComponentProps} from 'react';
import {tabletAndUpLinaria} from '../../../constants/customMedia';
import {useGetAriaLabelFromUrl} from '../../../hooks/useGetAriaLabelFromUrl/useGetAriaLabelFromUrl';
import {ImageType} from '../../../types/Image';
import {Button} from '../../atoms/Buttons/Button';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {Image} from '../../atoms/Image/Image';
import {Breakpoints, Ratio, Widths} from '../../atoms/Image/types';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';
import {VideoType} from '../../atoms/Video/type';
import {VideoOrFallbackImage} from '../../atoms/VideoOrFallbackImage/VideoOrFallbackImage';

interface CardImageProps {
  ['data-testid']?: string;
  className?: string;
  style?: React.CSSProperties;
  image: ImageType;
  imageRatio: Ratio;
  imageLazy?: boolean;
  video?: VideoType;
  breakpoints: Breakpoints;
  cta: {label?: string; url: string};
  showCtaOnHover?: boolean;
  onClick?: React.MouseEventHandler;
  ctaSize?: ComponentProps<typeof Button>['size'];
  widths?: Widths;
}

export const CardImage = ({
  className,
  style,
  'data-testid': dataTestId,
  image,
  video,
  imageRatio,
  breakpoints,
  cta,
  showCtaOnHover,
  onClick,
  children,
  ctaSize = 'fit-width',
  widths,
  imageLazy,
  ref,
  ...rest
}: React.PropsWithChildren<CardImageProps> & {
  ref?: React.Ref<HTMLAnchorElement>;
}) => {
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();

  return (
    <Wrapper
      className={className}
      style={style}
      ref={ref}
      data-show-cta-on-hover={cta.label && showCtaOnHover}
      data-testid={dataTestId}
      to={cta.url}
      onClick={onClick}
      aria-label={getAriaLabelFromUrl(cta.url)}
      {...rest}
    >
      <VideoOrFallbackImage
        video={video}
        image={
          <StyledImage
            data-testid="coverImage"
            src={image.url}
            alt={image.description}
            ratio={imageRatio}
            breakpoints={breakpoints}
            widths={widths}
            loading={imageLazy ? 'lazy' : 'eager'}
          />
        }
      />
      <Content>
        {children}
        {cta.label && (
          <CTA aria-label={undefined} variant="glassy" size={ctaSize}>
            {cta.label}
            <Icon
              asset={GenericIconName.RightArrowSharpEdges}
              size="XS"
              fill="White"
            />
          </CTA>
        )}
      </Content>
    </Wrapper>
  );
};

export const Content = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 10px;
  z-index: 1;
  transition: transform 0.25s ease-out;
`;

export const CTA = styled(Button)``;

const Wrapper = styled(UnstyledLinkLinaria)`
  position: relative;
  display: flex;
  overflow: hidden;
  border-radius: 8px;

  &::after {
    position: absolute;
    width: 100%;
    height: 100%;
    inset: 0;
    content: '';
    transition: transform 0.15s ease-in;
    transform: scaleY(0.8);
    transform-origin: bottom;
    background: linear-gradient(
      360deg,
      rgba(0, 0, 0, 0.75) 0%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  ${tabletAndUpLinaria(`
    &::after {
      transform: scaleY(0.25);
    }
  `)}

  &[data-show-cta-on-hover='true'] {
    ${Content} {
      transform: translateY(50px);
    }

    ${CTA} {
      opacity: 0;
    }

    &:hover {
      ${Content} {
        transform: translateY(0);
      }

      ${CTA} {
        opacity: 1;
      }
    }
  }
`;

const StyledImage = styled(Image)`
  object-fit: cover;
  object-position: top;
  height: 100%;
`;
